import { RouteActionsType, RouteTypeSettingsEnum, SurgeonTrip } from '@/api/apis/mission-planner';
import { queryClient } from '@/queries';
import { caseQueryKeys } from '@/queries/case';
import { Case, CaseResourceType } from '@/api/apis/case';
import { RouteContextResourcesType, RoutePlanResources, RoutePlanType } from '@/types/route';
import { MultiSuggestChainItem, ResourceWarningEnum } from '@/api/apis/multi-suggest';
import {
  convertCommercialFlights,
  convertSurgeonTrips,
  getRoutePlan,
  mergeCommercialWithMainRoute,
  mergeSurgeonAndMainRoute,
} from '@/utils/route/algorithm';
import { getDefaultDryRunSegments, getDefaultSegments, sortSegmentsByTime } from '@/utils/route/segment';
import { resourceAvailabilityQueries } from '@/queries/resource/availability';
import { ocsConsoleQueries } from '@/queries/ocs-consoles';
import { getTimeZonedDate } from '@/utils/helpers';
import { OPERATION_TIME_BY_ORGAN } from '@/utils/route/config';
import { multiSuggestQueryClient } from '@/queries/multi-suggest';
import { AircraftLogisticsResourceType } from '@/api/apis/route';

export const fetchPlanResources = async (
  resources: Partial<RoutePlanResources>,
): Promise<RouteContextResourcesType> => {
  const csResource = resources.ClinicalSpecialist?.resourceId
    ? await queryClient.ensureQueryData(
        resourceAvailabilityQueries.employeeById({ employeeId: resources.ClinicalSpecialist.resourceId }),
      )
    : null;

  const rsResource = resources.Surgeon?.resourceId
    ? await queryClient.ensureQueryData(
        resourceAvailabilityQueries.employeeById({ employeeId: resources.Surgeon.resourceId }),
      )
    : null;

  const ocsResource = resources.OCSConsole?.resourceId
    ? await queryClient.ensureQueryData(ocsConsoleQueries.item(resources.OCSConsole.resourceId, { withDeleted: true }))
    : null;

  return {
    [CaseResourceType.ClinicalSpecialist]: csResource
      ? [
          {
            resourceId: csResource.id,
            resourceType: CaseResourceType.ClinicalSpecialist,
            data: csResource,
          },
        ]
      : [],
    [CaseResourceType.Surgeon]: rsResource
      ? [
          {
            resourceId: rsResource.id,
            resourceType: CaseResourceType.Surgeon,
            data: rsResource,
          },
        ]
      : [],
    [CaseResourceType.OCSConsole]: ocsResource
      ? [
          {
            resourceId: ocsResource.id,
            resourceType: CaseResourceType.OCSConsole,
            data: ocsResource,
          },
        ]
      : [],
    [CaseResourceType.Aircraft]: resources.Aircraft?.length
      ? resources.Aircraft.map<AircraftLogisticsResourceType>((i) => ({
          resourceId: i.id,
          resourceType: CaseResourceType.Aircraft,
          data: i.data,
          tailNumber: i.data?.registration || '',
        }))
      : [],
    [CaseResourceType.Crew]: [],
  };
};

type ConvertAutosuggestRouteParams = {
  mainTrip: MultiSuggestChainItem;
  surgeonTrip?: SurgeonTrip;
  commercialTrip?: RouteActionsType;
};

export type MultiSuggestRoutePlanType = Omit<RoutePlanType, 'resources'> & {
  caseId: string;
  resources: RouteContextResourcesType;
  hasSurgeonTrip: boolean;
  hasRealCommercialFlight: boolean;
  orTime: string;
  ocsWarning?: ResourceWarningEnum;
  specialistWarning?: ResourceWarningEnum;
};

export const convertAutosuggestRoute = async ({
  mainTrip,
  surgeonTrip,
  commercialTrip,
}: ConvertAutosuggestRouteParams): Promise<MultiSuggestRoutePlanType | null> => {
  try {
    const currentCase = multiSuggestQueryClient.getQueryData<Case>(caseQueryKeys.getItem(mainTrip.caseId));

    if (!currentCase) {
      return null;
    }

    const orDateTime = getTimeZonedDate(
      currentCase.donorHospitalDate,
      currentCase.donorHospitalTime,
      currentCase.donorHospitalTimeZone,
    );
    const donorHospitalDateTimeString = orDateTime?.toString() || null;
    const donorOperationEndDateTimeString = orDateTime
      ?.add({ milliseconds: OPERATION_TIME_BY_ORGAN[currentCase.organType] })
      .toString();

    const defaultSegments = getDefaultSegments({
      donorHospital: currentCase.donorHospitalDetails!,
      txCenter: currentCase.txCenterDetails,
      donorHospitalDateTimeString,
      donorOperationEndDateTimeString,
    });
    const defaultDryRunSegments = getDefaultDryRunSegments(currentCase.donorHospitalDetails!);

    const mainRoutePlan = await getRoutePlan({
      txCenter: currentCase.txCenterDetails!,
      donorHospital: currentCase.donorHospitalDetails,
      plan: mainTrip.plan,
      defaultSegments,
      dryRunDefaultSegments: defaultDryRunSegments,
      planType: RouteTypeSettingsEnum.Full,
      isTmdxDriveCompatible: true,
    });
    const surgeonRoutePlan = surgeonTrip ? await convertSurgeonTrips(surgeonTrip, true) : null;
    const commercialRoutePlan = commercialTrip?.actions.length
      ? await convertCommercialFlights(commercialTrip, mainRoutePlan.teamResources, true)
      : null;

    const route = commercialRoutePlan
      ? mergeCommercialWithMainRoute(mainRoutePlan, commercialRoutePlan)
      : mainRoutePlan;

    const completeSurgeonRoute = surgeonRoutePlan ? mergeSurgeonAndMainRoute(route, surgeonRoutePlan) : {};

    const completeRoute = {
      ...route,
      ...completeSurgeonRoute,
    };

    const resources = await fetchPlanResources(completeRoute.resources);

    return {
      ...completeRoute,
      caseId: mainTrip.caseId,
      segments: sortSegmentsByTime(completeRoute.segments),
      resources,
      orTimeDiffMs: route.orTimeDiffMs,
      hasSurgeonTrip: !!surgeonTrip,
      hasRealCommercialFlight: !!commercialTrip?.actions.length,
      orTime: mainTrip.orTime,
      ocsWarning: mainTrip.ocsWarning,
      specialistWarning: mainTrip.specialistWarning,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return null;
  }
};
